// src/firebaseAuth.js

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";

import {
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

import {
  educationAuth,
  educationDb,
  educationStorage,
} from "./firebase.js";

/*
|--------------------------------------------------------------------------
| INSCRIPTION HDC ÉDUCATION
|--------------------------------------------------------------------------
|
| Cette fonction utilise uniquement :
|
| - educationAuth ;
| - educationDb ;
| - educationStorage.
|
| Une inscription Éducation ne sera donc plus associée à la session
| Consulting/Finance du navigateur.
|
*/

export const registerUser = async (
  email,
  password,
  profileData = {}
) => {
  try {
    /*
     * Création du compte dans la session Éducation.
     */
    const userCredential =
      await createUserWithEmailAndPassword(
        educationAuth,
        email.trim(),
        password
      );

    const user = userCredential.user;

    /*
     * Nettoyage du numéro de téléphone.
     */
    const telephone = (
      profileData.telephone || ""
    )
      .toString()
      .replace(/\s+/g, "")
      .trim();

    /*
     * Téléversement facultatif de la photo de profil.
     */
    let photoURL = "";
    const photoFile = profileData.photoFile;

    if (
      typeof File !== "undefined" &&
      photoFile instanceof File
    ) {
      const safeName = (
        photoFile.name || "photo"
      ).replace(/[^a-zA-Z0-9._-]/g, "_");

      const fileReference = ref(
        educationStorage,
        `educationUsers/${user.uid}/${Date.now()}_${safeName}`
      );

      await uploadBytes(fileReference, photoFile, {
        contentType:
          photoFile.type || "image/jpeg",
      });

      photoURL = await getDownloadURL(
        fileReference
      );
    }

    /*
     * Nom complet utilisé dans Firebase Authentication.
     */
    const displayName = `${(
      profileData.prenom || ""
    ).trim()} ${(
      profileData.nom || ""
    ).trim()}`.trim();

    /*
     * Mise à jour du profil Firebase Authentication.
     */
    if (displayName || photoURL) {
      await updateProfile(user, {
        displayName:
          displayName ||
          user.displayName ||
          "",

        photoURL:
          photoURL ||
          user.photoURL ||
          "",
      });
    }

    /*
     * Création du profil dans Firestore.
     *
     * La collection educationUsers est séparée de la collection users
     * éventuellement utilisée par Consulting ou Finance.
     */
    await setDoc(
      doc(
        educationDb,
        "educationUsers",
        user.uid
      ),
      {
        uid: user.uid,
        email: user.email || email.trim(),

        prenom: (
          profileData.prenom || ""
        ).trim(),

        nom: (
          profileData.nom || ""
        ).trim(),

        age:
          profileData.age !== undefined &&
          profileData.age !== ""
            ? Number(profileData.age)
            : null,

        profession: (
          profileData.profession || ""
        ).trim(),

        residence: (
          profileData.residence || ""
        ).trim(),

        telephone,

        countryCode: (
          profileData.countryCode || ""
        ).trim(),

        /*
         * Rôle Éducation :
         * eleve, tuteur ou ecole.
         */
        role: (
          profileData.role || "eleve"
        )
          .toString()
          .trim()
          .toLowerCase(),

        photoURL:
          photoURL ||
          user.photoURL ||
          "",

        platform: "education",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
    );

    console.log(
      "✅ Compte et profil Éducation créés :",
      user.email
    );

    return user;
  } catch (error) {
    console.error(
      "❌ Erreur pendant l’inscription Éducation :",
      error
    );

    throw error;
  }
};

/*
|--------------------------------------------------------------------------
| CONNEXION HDC ÉDUCATION
|--------------------------------------------------------------------------
*/

export const loginUser = async (
  email,
  password
) => {
  const userCredential =
    await signInWithEmailAndPassword(
      educationAuth,
      email.trim(),
      password
    );

  return userCredential.user;
};

/*
|--------------------------------------------------------------------------
| DÉCONNEXION HDC ÉDUCATION
|--------------------------------------------------------------------------
|
| Cette fonction déconnecte uniquement educationAuth.
| La session Finance reste ouverte.
|
*/

export const logoutUser = async () => {
  await signOut(educationAuth);
};

/*
|--------------------------------------------------------------------------
| EXPORT DE L’AUTHENTIFICATION ÉDUCATION
|--------------------------------------------------------------------------
*/

export { educationAuth };

export default educationAuth;